import { useRouter } from 'next/router';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon } from 'components';
import SemiCircleProgressBar from 'components/SemiCircleProgress';
import { Toggle } from 'components/form';

import ProgressBar from 'components/ComponentV2/ProgressBar';
import { useEvent } from 'hooks/useEvent';

import { EventTouchpointable } from 'lib/models/event';
import { parseResponse } from 'lib/utils/parser';

import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from './Feedback.module.scss';

export const Feedback = (): ReactElement => {
  const router = useRouter();
  const { id } = router.query;
  const { toggleCollectFeedback } = useEvent();

  const [t] = useTranslation('event-dashboard');

  const { touchpoint } = useAttendeesFilter();
  let noOfRating = 0;

  const { rating_map, touchpointable, cohost_application } = touchpoint || {};
  const isHost = !cohost_application ? true : false;

  const [collectFeedback, setCollectFeedback] = useState<boolean>(false);

  useEffect(() => {
    if (touchpointable) {
      setCollectFeedback(
        parseResponse(touchpointable as EventTouchpointable).collect_feedback
      );
    }
  }, [touchpointable]);

  const calculateRating = useMemo(() => {
    if (rating_map) {
      let sumOfRating = 0;
      for (const [key, value] of Object.entries(rating_map)) {
        sumOfRating += parseInt(key) * value;
        noOfRating += value;
      }
      return noOfRating ? (sumOfRating / noOfRating) * 20 : 0;
    }
    return 0;
  }, [rating_map]);

  const getDetails = useMemo(() => {
    if (rating_map) {
      const details = [];
      for (const [key, value] of Object.entries(rating_map)) {
        details.push(
          <div className={styles.detailsRow}>
            <span className={styles.label}>
              {key}
              <Icon
                iconName="icon_star"
                size="small"
                className={styles.labelIcon}
              />
            </span>
            <ProgressBar
              percentCompleted={(value / noOfRating) * 100}
              color="Primary"
              className={styles.progressBar}
              showProgressWhen0={false}
            />
            <span>
              {((value / noOfRating) * 100).toFixed(0)}% ({value})
            </span>
          </div>
        );
      }
      return details.reverse();
    }
  }, [rating_map]);

  return (
    <section className={styles.feedback}>
      <div className={styles.feedbackHeader}>
        <p>{t('feedback.title')}</p>
        {isHost && (
          <div className={styles.toggleWrapper}>
            <span className={styles.text}>
              {t('feedback.collect-feedback')}
            </span>
            <Toggle
              checked={collectFeedback}
              onChange={() => {
                setCollectFeedback(!collectFeedback);
                toggleCollectFeedback(
                  !collectFeedback,
                  touchpointable.data.id,
                  id as string
                );
              }}
            />
          </div>
        )}
      </div>
      {!calculateRating ? (
        <div className={styles.emptyFeedback}>
          <Icon iconName="icon_info" size="large" />
          <p>{t('feedback.no-feedback')}</p>
        </div>
      ) : (
        <div className={styles.ratingWrapper}>
          <div className={styles.rating}>
            <div className={styles.ratingDisplay}>
              <p className={styles.ratingText}>{t('feedback.event-rating')}</p>
              <p className={styles.ratingNumber}>
                {(calculateRating / 20).toFixed(1)}
              </p>
            </div>

            <SemiCircleProgressBar
              percentage={calculateRating}
              diameter={180}
              strokeWidth={15}
              stroke={'#009491'}
              background={'#F3F3F3'}
              orientation={'down'}
              showPercentValue={false}
            />
          </div>
          <div className={styles.details}>{getDetails}</div>
        </div>
      )}
    </section>
  );
};
